'use client';
import { EmblaCarouselType } from 'embla-carousel';
import { useEffect, useMemo, useState } from 'react';

import { sendGTMEvent } from '@/fo/api/gtm';
import NaosIcon from '@/fo/icons/base/icons/naos.svg';
import { NavButton } from '@/fo/ui/category/carousel/base';
import { UiKitCarouselBase } from '@/fo/ui/kit/carousel/base';
import { UiKitCheckboxTag } from '@/fo/ui/kit/checkbox/tag';
import { UiModHeadFixed, useStickyVisibility } from '@/fo/ui/mod/head/fixed';
import { useMediaQuery } from '@/fo/ui/mod/media/query';

import { FilterLabelList, FilterOption, SelectedFilters } from '../../types';
import { sortSelectedFirst } from '../../util/sortSelectedFirst';
import { PickUpCare } from '../PickUpCare/PickUpCare';

import { styles } from './CareTagsList.styles';

type NativeElement = React.HTMLAttributes<HTMLDivElement>;

export interface CareTagsListProps {
  onClick?: NativeElement['onClick'];
  tagsList: FilterOption[];
  pickUpCareFilters: FilterLabelList[];
  selectedFilters: SelectedFilters;
  className?: string;
  onReset: () => void;
  onShowProducts: () => void;
  productsCount: number;
  onOpenChange?: (open: boolean) => void;
  onFilterChange: (
    slug: string,
    value: string | [number, number],
    checked: boolean,
    withUpdate?: boolean
  ) => void;
  isLoading?: boolean;
  isOpenModal?: boolean;
  gtmEventCategory?: string;
}

export function CareTagsList({
  tagsList,
  pickUpCareFilters,
  selectedFilters,
  className,
  onReset,
  onShowProducts,
  productsCount,
  onOpenChange,
  onFilterChange,
  isLoading = false,
  isOpenModal = false,
  gtmEventCategory,
  ...props
}: CareTagsListProps) {
  const slots = styles();
  const isDesktop = useMediaQuery('sm');

  const { showSticky, rootRef } = useStickyVisibility();

  const [emblaApi, setEmblaApi] = useState<EmblaCarouselType>();

  useEffect(() => {
    if (emblaApi) {
      const toggleActiveWhenScrollable = () => {
        const isScrollable = emblaApi.internalEngine().scrollSnaps.length > 1;
        emblaApi.reInit({ active: isScrollable });
      };

      emblaApi.on('resize', toggleActiveWhenScrollable);
      toggleActiveWhenScrollable();
    }
  }, [emblaApi]);

  const handleFilterChange = (
    slug: string,
    value: string | [number, number],
    checked: boolean,
    withUpdate?: boolean
  ) => {
    if (checked) {
      emblaApi?.scrollTo(0);
    }
    if (
      checked &&
      typeof value === 'string' &&
      tagsList.some((tag) => tag.value === value)
    ) {
      const label = tagsList.find((tag) => tag.value === value)?.label || value;
      sendGTMEvent({
        event: 'catalog_menu_click',
        eventCategory: gtmEventCategory || '',
        eventLabel: label
      } as Parameters<typeof sendGTMEvent>[0]);
    }
    onFilterChange(slug, value, checked, withUpdate);
  };

  const sortedTagsList = useMemo(() => {
    return [...tagsList].sort(sortSelectedFirst(selectedFilters));
  }, [tagsList, selectedFilters]);

  const contentNode = (
    <div className={slots.container({ withoutGap: tagsList.length === 0 })}>
      <PickUpCare
        className={slots.pickUpCareMobile()}
        pickUpCareFilters={pickUpCareFilters}
        selectedFilters={selectedFilters}
        onTagClick={handleFilterChange}
        onReset={onReset}
        onShowProducts={onShowProducts}
        productsCount={productsCount}
        onOpenChange={onOpenChange}
        isLoading={isLoading}
        isOpenModal={isOpenModal}
        gtmEventCategory={gtmEventCategory}
      />
      <UiKitCarouselBase
        showArrows={false}
        className={slots.carousel()}
        viewportClassName={slots.viewport()}
        loop={false}
        withScrollGestures
        skipSnaps
        onInit={setEmblaApi}
        slidesToScroll={2}
        renderCustomControls={(controls) => (
          <div className={slots.controls()}>
            <div className={slots.controlsContainer()}>
              <div className={slots.controlsInner()}>
                <NavButton
                  disabled={controls.prevBtnDisabled}
                  onClick={controls.onPrevButtonClick}
                  className={slots.controlsButton()}
                />
                <NavButton
                  disabled={controls.nextBtnDisabled}
                  direction="next"
                  onClick={controls.onNextButtonClick}
                  className={slots.controlsButton()}
                />
              </div>
            </div>
          </div>
        )}
      >
        {isDesktop && pickUpCareFilters.length > 0 && (
          <PickUpCare
            className={slots.pickUpCare()}
            pickUpCareFilters={pickUpCareFilters}
            selectedFilters={selectedFilters}
            onTagClick={handleFilterChange}
            onReset={onReset}
            onShowProducts={onShowProducts}
            productsCount={productsCount}
            onOpenChange={onOpenChange}
            isLoading={isLoading}
            isOpenModal={isOpenModal}
            gtmEventCategory={gtmEventCategory}
          />
        )}
        {isDesktop && tagsList.length > 0 && pickUpCareFilters.length > 0 && (
          <div className={slots.naosIconWrapper()}>
            <NaosIcon />
          </div>
        )}
        {sortedTagsList.map((tag, index) => (
          <UiKitCheckboxTag
            key={`${tag.value}-${index}`}
            onChange={(checked) =>
              handleFilterChange(tag.slug || '', tag.value, checked, true)
            }
            checked={
              tag.slug && selectedFilters?.[tag.slug]
                ? (selectedFilters[tag.slug] as string[]).includes(tag.value)
                : false
            }
            label={tag.label}
          />
        ))}
      </UiKitCarouselBase>
    </div>
  );

  if (pickUpCareFilters.length === 0 && tagsList.length === 0) return null;

  return (
    <>
      <div
        className={slots.base({ className })}
        ref={rootRef}
      >
        {contentNode}
      </div>
      <UiModHeadFixed
        {...props}
        className={slots.base()}
        order={1}
        show={showSticky}
        positioning="fixed"
      >
        {contentNode}
      </UiModHeadFixed>
    </>
  );
}
